import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronDown, Clock3, Loader2, MapPin } from 'lucide-react';
import { useVoucherStore } from '../stores/voucherStore';
import { useCampaignStore } from '../stores/campaignStore';
import { apiClient } from '../services/api';
import { setInsiderUserProfile } from '../integrations/insider/insider.user';
import { trackVoucherRegistration } from '../integrations/insider/insider.events';
import { normalizeCpfAndHash } from '../integrations/insider/insider.identity';
import { StepProgress } from '../components/StepProgress';
import { CampaignHeader } from '../components/CampaignHeader';
import { useAnalytics } from '../hooks/useAnalytics';
import { isValidCpf } from '../utils/validateCpf';
import { isOlderThan } from '../utils/validateAge';

const MINIMUM_AGE = 18;
const UNDERAGE_MESSAGE = 'É necessário ter 18 anos ou mais para participar da campanha!';

interface StateEntry {
  state: string;
  uf: string;
  cities: { name: string; la: number; lg: number }[];
}

interface CampaignStore {
  city?: string | null;
  state?: string | null;
}

const normalizeLocation = (value: string) => value
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .trim()
  .toUpperCase();

const normalizeSearchText = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('pt-BR');

export function Step1Registration() {
  const navigate = useNavigate();
  const { campaignSlug } = useParams<{ campaignSlug: string }>();
  const { customer, setCurrentStep, updateCustomer, updateReservation, setUserLocation } = useVoucherStore();
  const { campaign } = useCampaignStore();
  const { trackException, trackSelectContent } = useAnalytics();
  const cpfInputRef = useRef<HTMLInputElement>(null);
  
  const [form, setForm] = useState({ 
    name: '', 
    cpf: '', 
    birthDate: '',
    email: '', 
    phone: '',
    gender: '',
    state: '',
    city: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [statesData, setStatesData] = useState<StateEntry[]>([]);
  const [cities, setCities] = useState<{ name: string; la: number; lg: number }[]>([]);
  const [cityQuery, setCityQuery] = useState('');
  const [isCityListOpen, setIsCityListOpen] = useState(false);
  const [highlightedCityIndex, setHighlightedCityIndex] = useState(0);
  const [loadingUfs, setLoadingUfs] = useState(true);
  const [loadingCities] = useState(false);
  const [acceptedCampaignRules, setAcceptedCampaignRules] = useState(Boolean(customer?.consentTerms));
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(Boolean(customer?.consentLgpd));
  const [acceptedMarketing, setAcceptedMarketing] = useState(customer?.consentNotifications ?? true);
  const [showCpfModal, setShowCpfModal] = useState(false);
  const [showRedeemedModal, setShowRedeemedModal] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [requestingLocation, setRequestingLocation] = useState(false);
  const [existingVoucher, setExistingVoucher] = useState<{
    voucherId: string;
    voucherCode: string;
    storeName: string;
    redemptionDate: string;
    qrCodeToken: string;
  } | null>(null);

  // Load states from static JSON
  useEffect(() => {
    const loadStates = async () => {
      try {
        setLoadingUfs(true);
        const res = await fetch('/data/cities_by_state.json');
        const data: StateEntry[] = await res.json();
        const stores = (campaign?.stores || []) as CampaignStore[];
        const availableLocations = new Set(
          stores
            .filter((store) => store.city && store.state)
            .map((store) => `${normalizeLocation(store.state as string)}|${normalizeLocation(store.city as string)}`),
        );
        setStatesData(data
          .map((stateEntry) => ({
            ...stateEntry,
            cities: stateEntry.cities.filter((city) => availableLocations.has(
              `${normalizeLocation(stateEntry.uf)}|${normalizeLocation(city.name)}`,
            )),
          }))
          .filter((stateEntry) => stateEntry.cities.length > 0));
      } catch (error) {
        console.error('Error loading states:', error);
        trackException('registration_load_states_failed');
        setErrors((prev) => ({ ...prev, state: 'Erro ao carregar estados' }));
      } finally {
        setLoadingUfs(false);
      }
    };

    loadStates();
  }, [campaign]);

  useEffect(() => {
    if (!customer) return;

    setForm((previous) => ({
      ...previous,
      name: customer.name || previous.name,
      cpf: customer.cpf ? maskCpf(customer.cpf) : previous.cpf,
      birthDate: customer.birthDate || previous.birthDate,
      email: customer.email || previous.email,
      phone: customer.phone ? maskPhone(customer.phone) : previous.phone,
      state: customer.state || previous.state,
      city: customer.city || previous.city,
      gender: customer.gender || previous.gender,
    }));
    setCityQuery(customer.city || '');
    setAcceptedCampaignRules(Boolean(customer.consentTerms));
    setAcceptedPrivacy(Boolean(customer.consentLgpd));
    setAcceptedMarketing(customer.consentNotifications ?? true);
  }, [customer]);

  // Update cities when state changes
  useEffect(() => {
    if (!form.state) {
      setCities([]);
      return;
    }

    if (!statesData.length) return;

    const stateEntry = statesData.find((s) => normalizeLocation(s.uf) === normalizeLocation(form.state));
    const nextCities = stateEntry?.cities || [];
    setCities(nextCities);
    setForm((prev) => (
      prev.city && nextCities.some((city) => normalizeLocation(city.name) === normalizeLocation(prev.city))
        ? prev
        : { ...prev, city: '' }
    ));
    setCityQuery((previous) => (
      form.city && nextCities.some((city) => city.name === form.city) ? form.city : ''
    ));
    setHighlightedCityIndex(0);
  }, [form.state, statesData]);

  const filteredCities = cities.filter((city) =>
    normalizeSearchText(city.name).includes(normalizeSearchText(cityQuery.trim()))
  );

  const selectCity = (cityName: string) => {
    handleChange('city', cityName);
    setCityQuery(cityName);
    setIsCityListOpen(false);
    setHighlightedCityIndex(0);
  };

  const handleCityKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!filteredCities.length) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setIsCityListOpen(true);
      setHighlightedCityIndex((index) => (index + 1) % filteredCities.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setIsCityListOpen(true);
      setHighlightedCityIndex((index) => (index - 1 + filteredCities.length) % filteredCities.length);
    } else if (event.key === 'Enter' && isCityListOpen) {
      event.preventDefault();
      selectCity(filteredCities[highlightedCityIndex].name);
    } else if (event.key === 'Escape') {
      setCityQuery(form.city);
      setIsCityListOpen(false);
    }
  };

  const maskCpf = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 11);
    let masked = digits;
    if (digits.length > 3) masked = digits.slice(0, 3) + '.' + digits.slice(3);
    if (digits.length > 6) masked = masked.slice(0, 7) + '.' + digits.slice(6);
    if (digits.length > 9) masked = masked.slice(0, 11) + '-' + digits.slice(9);
    return masked;
  };

  const maskPhone = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 11);
    if (digits.length <= 2) return digits.length ? `(${digits}` : '';
    if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  };

  const handleChange = (field: string, value: string) => {
    let masked = value;
    if (field === 'cpf') masked = maskCpf(value);
    if (field === 'phone') masked = maskPhone(value);
    if (field === 'email') masked = value.toLowerCase();
    setForm((prev) => ({ ...prev, [field]: masked }));
    if (field === 'name' || field === 'cpf' || field === 'birthDate' || field === 'email' || field === 'phone' || field === 'gender' || field === 'state' || field === 'city') {
      updateCustomer({ [field]: masked });
    }
    setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const handleCpfBlur = async () => {
    const digits = form.cpf.replace(/\D/g, '');
    if (digits.length !== 11) {
      setErrors((previous) => ({ ...previous, cpf: 'CPF deve conter 11 dígitos' }));
      return;
    }
    if (!isValidCpf(digits)) {
      setErrors((previous) => ({ ...previous, cpf: 'CPF inválido' }));
      return;
    }
    if (!campaign?.id) return;

    setErrors((previous) => ({ ...previous, cpf: '' }));

    try {
      const { data } = await apiClient.get(`/customer/campaigns/${campaign.id}/check-cpf`, {
        params: { cpf: digits },
      });
      if (data.hasReservation) {
        setExistingVoucher(data);
        setShowCpfModal(true);
      } else if (data.hasRedemption) {
        setExistingVoucher(data);
        setShowRedeemedModal(true);
      }
    } catch {
      // Silently ignore — non-blocking check
    }
  };

  const handleBirthDateBlur = () => {
    if (form.birthDate && !isOlderThan(form.birthDate, MINIMUM_AGE)) {
      setErrors((previous) => ({ ...previous, birthDate: UNDERAGE_MESSAGE }));
    }
  };

  const handleConsultVoucher = () => {
    if (!existingVoucher) return;
    setShowCpfModal(false);
    updateReservation({
      storeName: existingVoucher.storeName,
      scheduledDate: existingVoucher.redemptionDate,
      voucherCode: existingVoucher.voucherCode,
    });
    updateCustomer({ cpf: form.cpf.replace(/\D/g, '') });
    setCurrentStep(4);
    navigate(`/${campaignSlug}/confirmacao`);
  };

  const handleConsultRedeemedVoucher = () => {
    if (!existingVoucher) return;
    setShowRedeemedModal(false);
    const data = existingVoucher as { storeName: string; storeCity?: string; storeState?: string; redemptionDate: string; voucherCode: string; redeemedAt?: string };
    updateReservation({
      storeName: data.storeName,
      storeCity: data.storeCity,
      storeState: data.storeState,
      scheduledDate: data.redemptionDate,
      voucherCode: data.voucherCode,
      redeemedAt: data.redeemedAt,
    });
    updateCustomer({ cpf: form.cpf.replace(/\D/g, '') });
    navigate(`/${campaignSlug}/voucher-resgatado`);
  };

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!form.name.trim()) errs.name = 'Nome é obrigatório';
    const cpfDigits = form.cpf.replace(/\D/g, '');
    if (cpfDigits.length !== 11) errs.cpf = 'CPF deve conter 11 dígitos';
    else if (!isValidCpf(cpfDigits)) errs.cpf = 'CPF inválido';
    if (!form.birthDate) errs.birthDate = 'Data de nascimento é obrigatória';
    else if (!isOlderThan(form.birthDate, MINIMUM_AGE)) errs.birthDate = UNDERAGE_MESSAGE;
    if (!form.email.includes('@')) errs.email = 'E-mail inválido';
    if (form.phone.replace(/\D/g, '').length < 10) errs.phone = 'Telefone inválido';
    if (!form.gender) errs.gender = 'Gênero é obrigatório';
    if (!form.state) errs.state = 'Estado é obrigatório';
    if (!form.city) errs.city = 'Cidade é obrigatória';
    if (!acceptedCampaignRules) errs.campaignRules = 'É necessário aceitar o regulamento da campanha';
    if (!acceptedPrivacy) errs.privacy = 'É necessário aceitar a Política de Privacidade Riachuelo';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const [submitting, setSubmitting] = useState(false);

  const goToStores = () => {
    setShowLocationModal(false);
    setCurrentStep(2);
    navigate(`/${campaignSlug}/lojas`);
  };

  const handleRequestLocation = () => {
    if (!navigator.geolocation) {
      goToStores();
      return;
    }

    setRequestingLocation(true);
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        setUserLocation({ lat: coords.latitude, lng: coords.longitude });
        goToStores();
      },
      () => {
        setRequestingLocation(false);
        goToStores();
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 },
    );
  };

  const handleSubmit = async () => {
    trackSelectContent('button', 'enviar_cadastro');
    if (!validate()) return;

    const cpfDigits = form.cpf.replace(/\D/g, '');
    const phoneDigits = form.phone.replace(/\D/g, '');

    setSubmitting(true);
    try {
      const { data } = await apiClient.post('/customer/auth/register', {
        name: form.name,
        cpf: cpfDigits,
        email: form.email,
        phone: phoneDigits,
        password: cpfDigits, // Piloto: sem login, password = cpf
        birthDate: form.birthDate,
        gender: form.gender || null,
        state: form.state,
        city: form.city,
        consentLgpd: acceptedPrivacy,
        consentTerms: acceptedCampaignRules,
        consentPolicy: acceptedPrivacy,
        consentNotifications: acceptedMarketing,
      });

      // Store token for subsequent authenticated requests
      const TOKEN_KEY = import.meta.env.VITE_SESSION_TOKEN_KEY || 'cust_tk';
      sessionStorage.setItem(TOKEN_KEY, data.accessToken);

      // Insider: set user profile and track registration event
      const selectedState = statesData.find((s) => s.uf === form.state);
      void setInsiderUserProfile({
        nome: form.name,
        email: form.email,
        telefone: phoneDigits,
        dataNascimento: form.birthDate,
        genero: form.gender,
        cidade: form.city,
        estado: selectedState?.state || form.state,
        cpf: cpfDigits,
        consentTerms: acceptedCampaignRules,
        consentNotification: acceptedMarketing,
      });
      const cpfHash = await normalizeCpfAndHash(cpfDigits);
      trackVoucherRegistration({
        cpf_hash: cpfHash,
        campaign_name: campaign?.name || '',
        consent_terms: acceptedCampaignRules,
        consent_notification: acceptedMarketing,
      });

      updateCustomer({
        name: form.name,
        cpf: cpfDigits,
        birthDate: form.birthDate,
        email: form.email,
        phone: phoneDigits,
        state: form.state,
        city: form.city,
        consentLgpd: acceptedPrivacy,
        consentTerms: acceptedCampaignRules,
        consentNotifications: acceptedMarketing,
        customerId: data.customerId,
      });

      setShowLocationModal(true);
    } catch (err: unknown) {
      const error = err as { response?: { status?: number; data?: { detail?: string } } };
      trackException(`registration_failed_${error.response?.status || 'unknown'}`);
      if (error.response?.status === 409) {
        setErrors({ cpf: 'Este CPF já está cadastrado nesta campanha.' });
      } else {
        setErrors({ cpf: 'Erro ao realizar cadastro. Tente novamente.' });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <CampaignHeader />

      {/* Stepper */}
      <div className="max-w-lg mx-auto px-6 pt-6">
        <StepProgress currentStep={1} />
      </div>

      {/* Form */}
      <div className="max-w-lg mx-auto px-6 pb-8">
        <p className="mb-6 text-sm text-gray-500">Faça seu cadastro para garantir seu brinde</p>

        <div className="space-y-4">
          <Field 
            label="CPF" 
            value={form.cpf} 
            onChange={(v) => handleChange('cpf', v)} 
            onBlur={handleCpfBlur}
            error={errors.cpf} 
            placeholder="000.000.000-00" 
            maxLength={14}
            inputRef={cpfInputRef}
          />
          <Field 
            label="Nome completo" 
            value={form.name} 
            onChange={(v) => handleChange('name', v)} 
            error={errors.name} 
            placeholder="Seu nome" 
          />
          <Field
            label="Data de nascimento"
            value={form.birthDate}
            onChange={(v) => handleChange('birthDate', v)}
            onBlur={handleBirthDateBlur}
            error={errors.birthDate}
            type="date"
          />
          <Field 
            label="E-mail" 
            value={form.email} 
            onChange={(v) => handleChange('email', v)} 
            error={errors.email} 
            placeholder="seu@email.com" 
            type="email" 
          />
          <Field 
            label="CELULAR" 
            value={form.phone} 
            onChange={(v) => handleChange('phone', v)} 
            error={errors.phone} 
            placeholder="(11) 99999-9999" 
            maxLength={15} 
          />

          {/* Gender */}
          <div>
            <RequiredLabel>Gênero</RequiredLabel>
            <div className="relative">
              <select
                value={form.gender}
                onChange={(e) => handleChange('gender', e.target.value)}
                className={`w-full appearance-none bg-white border rounded-lg px-3 py-2 text-gray-900 focus:ring-2 focus:ring-rchlo-green focus:border-transparent cursor-pointer ${
                  errors.gender ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Selecione o gênero</option>
                <option value="Feminino">Feminino</option>
                <option value="Masculino">Masculino</option>
                <option value="Outro">Outro</option>
                <option value="Prefiro não informar">Prefiro não informar</option>
              </select>
              <ChevronDown size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>
            {errors.gender && <p className="text-xs text-red-500 mt-1">{errors.gender}</p>}
          </div>

          {/* State (UF) */}
          <div>
            <RequiredLabel>Estado</RequiredLabel>
            <div className="relative">
              <select
                value={form.state}
                onChange={(e) => handleChange('state', e.target.value)}
                disabled={loadingUfs}
                className={`w-full appearance-none bg-white border rounded-lg px-3 py-2 text-gray-900 focus:ring-2 focus:ring-rchlo-green focus:border-transparent cursor-pointer ${
                  errors.state ? 'border-red-500' : 'border-gray-300'
                } ${loadingUfs ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <option value="">{loadingUfs ? 'Carregando...' : 'Selecione um estado'}</option>
                {statesData.map((s) => (
                  <option key={s.uf} value={s.uf}>
                    {s.state}
                  </option>
                ))}
              </select>
              <ChevronDown size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>
            {errors.state && <p className="text-xs text-red-500 mt-1">{errors.state}</p>}
          </div>

          {/* City */}
          <div>
            <RequiredLabel>Cidade</RequiredLabel>
            <div className="relative">
              <input
                type="text"
                value={cityQuery}
                onChange={(e) => {
                  setCityQuery(e.target.value);
                  setIsCityListOpen(true);
                  setHighlightedCityIndex(0);
                  if (form.city) handleChange('city', '');
                }}
                onFocus={() => setIsCityListOpen(true)}
                onKeyDown={handleCityKeyDown}
                onBlur={() => {
                  window.setTimeout(() => {
                    setIsCityListOpen(false);
                    setCityQuery(form.city);
                  }, 150);
                }}
                disabled={!form.state || loadingCities}
                placeholder={loadingCities ? 'Carregando...' : form.state ? 'Digite a cidade...' : 'Selecione um estado primeiro'}
                role="combobox"
                aria-autocomplete="list"
                aria-expanded={isCityListOpen && filteredCities.length > 0}
                aria-controls="city-listbox"
                aria-activedescendant={isCityListOpen && filteredCities[highlightedCityIndex] ? `city-option-${highlightedCityIndex}` : undefined}
                className={`w-full bg-white border rounded-lg px-3 py-2 pr-10 text-gray-900 focus:ring-2 focus:ring-rchlo-green focus:border-transparent ${
                  errors.city ? 'border-red-500' : 'border-gray-300'
                } ${!form.state || loadingCities ? 'opacity-50 cursor-not-allowed' : ''}`}
              />
              <ChevronDown size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              {isCityListOpen && filteredCities.length > 0 && (
                <ul id="city-listbox" role="listbox" className="absolute z-20 mt-1 max-h-56 w-full overflow-auto rounded-lg border border-gray-300 bg-white py-1 shadow-lg">
                  {filteredCities.map((city, index) => (
                    <li
                      id={`city-option-${index}`}
                      key={city.name}
                      role="option"
                      aria-selected={form.city === city.name}
                      className={`cursor-pointer px-3 py-2 text-gray-900 ${index === highlightedCityIndex ? 'bg-gray-100' : 'hover:bg-gray-50'}`}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => selectCity(city.name)}
                    >
                      {city.name}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {errors.city && <p className="text-xs text-red-500 mt-1">{errors.city}</p>}
          </div>

          <div className="space-y-3 pt-1">
            <CheckboxField
              checked={acceptedCampaignRules}
              onChange={setAcceptedCampaignRules}
              error={errors.campaignRules}
            >
              Estou de acordo com o{' '}
              {campaign?.regulationPdfUrl ? (
                <a
                  href={campaign.regulationPdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(event) => event.stopPropagation()}
                  className="font-medium text-rchlo-green underline"
                >
                  regulamento da campanha
                </a>
              ) : (
                <span className="font-medium text-rchlo-green underline">regulamento da campanha</span>
              )}
            </CheckboxField>
            <CheckboxField
              checked={acceptedPrivacy}
              onChange={setAcceptedPrivacy}
              error={errors.privacy}
            >
              Estou de acordo com a{' '}
              {campaign?.faqPdfUrl ? (
                <a
                  href={campaign.faqPdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(event) => event.stopPropagation()}
                  className="font-medium text-rchlo-green underline"
                >
                  Política de Privacidade Riachuelo
                </a>
              ) : (
                <span className="font-medium text-rchlo-green underline">Política de Privacidade Riachuelo</span>
              )}
            </CheckboxField>
            <CheckboxField checked={acceptedMarketing} onChange={setAcceptedMarketing}>
              Aceito receber comunicação sobre promoções da Riachuelo por e-mail, SMS e WhatsApp
            </CheckboxField>
          </div>
        </div>

        <button onClick={handleSubmit} disabled={submitting} className="btn-primary w-full mt-8">
          {submitting ? 'Cadastrando...' : 'Próximo'}
        </button>
        <button
          type="button"
          onClick={() => navigate(`/${campaignSlug}`)}
          className="w-full mt-2 px-6 py-3 rounded-lg font-semibold text-gray-500 border border-gray-300 bg-white hover:bg-gray-50"
        >
          Voltar
        </button>
      </div>

      {/* Modal: location permission */}
      {showLocationModal && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/45">
          <div className="w-full max-w-lg rounded-t-3xl bg-white px-6 pb-8 pt-6 text-center animate-slide-up">
            <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-[#e8f5ef]">
              <MapPin size={28} className="text-rchlo-green" />
            </div>
            <h2 className="mb-2 text-base font-bold text-gray-900">Permitir acesso à localização?</h2>
            <p className="mx-auto mb-5 max-w-sm text-sm leading-5 text-gray-500">
              O site da Campanha Riachuelo quer usar sua localização para encontrar as lojas mais próximas de você e facilitar a escolha do ponto de resgate do seu brinde.
            </p>
            <div className="mb-4 flex items-center gap-3 rounded-lg bg-gray-50 px-3 py-3 text-left text-xs text-gray-400">
              <Clock3 size={16} />
              <span>campanha.riachuelo.com.br</span>
            </div>
            <button onClick={handleRequestLocation} disabled={requestingLocation} className="btn-primary w-full uppercase tracking-wide">
              {requestingLocation ? <Loader2 size={18} className="mx-auto animate-spin" /> : 'PERMITIR'}
            </button>
            <button onClick={goToStores} disabled={requestingLocation} className="mt-3 w-full rounded-lg border border-gray-200 bg-white px-6 py-3 font-semibold text-gray-500 hover:bg-gray-50">
              Não permitir
            </button>
          </div>
        </div>
      )}

      {/* Modal: CPF already has reservation */}
      {showCpfModal && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40">
          <div className="w-full max-w-lg bg-white rounded-t-3xl px-6 py-8 text-center animate-slide-up">
            <div className="w-14 h-14 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-5">
              <svg className="w-7 h-7 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.27 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h2 className="text-lg font-bold text-gray-900 mb-2">
              Este CPF já possui uma reserva ativa para esta campanha.
            </h2>
            <p className="text-sm text-gray-500 mb-6">
              Cada CPF pode ter apenas uma reserva ativa por campanha. Consulte seu voucher para ver os detalhes da sua reserva.
            </p>
            <button
              onClick={handleConsultVoucher}
              className="btn-primary w-full uppercase tracking-wide font-bold"
            >
              Consultar Voucher
            </button>
            <button
              onClick={() => {
                setShowCpfModal(false);
                setForm((prev) => ({ ...prev, cpf: '' }));
                setTimeout(() => cpfInputRef.current?.focus(), 100);
              }}
              className="w-full mt-3 px-6 py-3 rounded-lg font-semibold text-gray-500 border border-gray-300 bg-white hover:bg-gray-50"
            >
              Fechar
            </button>
          </div>
        </div>
      )}

      {/* Modal: CPF already redeemed */}
      {showRedeemedModal && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40">
          <div className="w-full max-w-lg bg-white rounded-t-3xl px-6 py-8 text-center animate-slide-up">
            <div className="w-14 h-14 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-5">
              <svg className="w-7 h-7 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-lg font-bold text-gray-900 mb-2">
              Este CPF já realizou o resgate de um brinde nesta campanha.
            </h2>
            <p className="text-sm text-gray-500 mb-6">
              Não é possível realizar uma nova reserva com este CPF. Consulte o voucher para conferir os detalhes do resgate.
            </p>
            <button
              onClick={handleConsultRedeemedVoucher}
              className="btn-primary w-full uppercase tracking-wide font-bold"
            >
              Consultar Voucher
            </button>
            <button
              onClick={() => {
                setShowRedeemedModal(false);
                setForm((prev) => ({ ...prev, cpf: '' }));
                setTimeout(() => cpfInputRef.current?.focus(), 100);
              }}
              className="w-full mt-3 px-6 py-3 rounded-lg font-semibold text-gray-500 border border-gray-300 bg-white hover:bg-gray-50"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, onBlur, error, placeholder, type = 'text', maxLength, inputRef }: {
  label: string; value: string; onChange: (v: string) => void; onBlur?: () => void; error?: string; placeholder?: string; type?: string; maxLength?: number; inputRef?: React.Ref<HTMLInputElement>;
}) {
  return (
    <div>
      <RequiredLabel>{label}</RequiredLabel>
      <input
        ref={inputRef}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        placeholder={placeholder}
        maxLength={maxLength}
        className={`input-field ${error ? 'border-red-500 focus:ring-red-200 focus:border-red-500' : ''}`}
      />
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
}

function RequiredLabel({ children }: { children: React.ReactNode }) {
  return (
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {children} <span className="text-red-500">*</span>
    </label>
  );
}

function CheckboxField({ checked, onChange, error, children }: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="flex items-start gap-3 text-sm text-gray-600 cursor-pointer">
        <input
          type="checkbox"
          checked={checked}
          onChange={(event) => onChange(event.target.checked)}
          className="mt-0.5 h-5 w-5 shrink-0 rounded border-gray-300 text-rchlo-green focus:ring-rchlo-green"
        />
        <span>{children}</span>
      </label>
      {error && <p className="text-xs text-red-500 mt-1 ml-8">{error}</p>}
    </div>
  );
}

